import { NmLoader } from '../components/NmLoader';
import { normalizeInputDigits } from '../utils/localizedInput';
import { displayDigits } from '../utils/displayDigits';
import { athleteSettingsApi } from '../services/athleteSettingsApi';
import '../styles/dashboard-annual-system.css';
import { IoArrowBackOutline, IoTrashOutline } from 'react-icons/io5';
import { ActionSymbol } from '../components/ui/ActionSymbol';
import { ActionButton, ActionIconButton, ActionToggleGroup } from '../components/ui/ActionButton';
import { prepareReviewTss } from './atp/helpers';
import '../styles/annual-plan-stepper.css';
import { AnnualPlanGuide } from './atp/AnnualPlanGuide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnnualPlanTranslation } from '../hooks/useAnnualPlanTranslation';
import { NmButton } from '../components/NmButton';
import { NmCard } from '../components/NmCard';
import { NmInput } from '../components/NmInput';
import { NmIsoDateField } from '../components/NmIsoDateField';
import { NmLoadingOverlay } from '../components/NmLoadingOverlay';
import { atpApi } from '../services/atpApi';
import type { ATPPlan, ATPWeek } from '../services/atpApi';
import type { CalendarEvent, UserProfile, TodayTrends } from '../services/api';
import { calendar, forms, home } from '../services/api';
import { AtpProgramView } from './atp/AtpProgramView';
import { PhaseTssSheet } from './atp/PhaseTssSheet';
import { AtpCtlTargetGauge } from './atp/AtpCtlTargetGauge';
import { WeeklyLoadSliderCard } from './atp/WeeklyLoadSliderCard';
import { EventFormModal } from './EventFormModal';
import {
  ATP_PHASES,
  DEFAULT_PHASE_COUNTS,
  DEFAULT_PHASE_WEEKS,
  TOTAL_WIZARD_STEPS,
  TRAINING_MODELS,
  type TrainingModelKey,
} from './atp/constants';
import {
  addDaysISO,
  buildPhasesPayload,
  computeARaceCtlMetrics,
  computeTotals,
  derivePhaseStateFromWeeks,
  distributePhaseWeekCounts,
  eventDateISO,
  extractSeasonEvents,
  flattenPhaseWeeklyTss,
  formatDateShort,
  getFirstAPriorityEvent,
  planEndISO,
  planStartISO,
  planWeeksSyncKey,
  projectCtlAtDate,
  redistributePlanWeeks,
  resolveRequiredPlanWeeks,
  resizeWeeklyTssForPhaseCounts,
  scalePhaseWeeklyTss,
  todayISO,
} from './atp/helpers';
import { CTL_GAUGE_MAX, CTL_GAUGE_MIN } from '../utils/gaugeGeometry';

const WIZARD_STEP_KEYS = [
  'initialInfo',
  'races',
  'ctlTarget',
  'trainingModel',
  'tssReview',
  'confirmBuild',
] as const;

// ─── Sub-components ─────────────────────────────────────────────────────────

function StepDots({ step, total }: { step: number; total: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '8px 0' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 7,
            width: i === step ? 22 : 7,
            borderRadius: 99,
            background:
              i < step
                ? 'var(--interaction-accent-border)'
                : i === step
                  ? 'var(--color-accent)'
                  : 'var(--surface-sunken)',
            transition: 'background-color 0.15s',
          }}
        />
      ))}
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div
      className="atp-wizard-stat"
      style={{
        flex: 1,
        textAlign: 'center',
        padding: '10px 8px',
        borderRadius: 'var(--r-2)',
        background: 'var(--surface-sunken)',
        boxShadow: 'var(--nm-flat)',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 800, color: color ?? 'var(--fg-1)', fontFamily: 'var(--font-mono)' }}>
        {displayDigits(value)}
      </div>
      <div
        style={{
          fontSize: 9,
          color: 'var(--fg-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginTop: 3,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--fg-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 6,
        textAlign: 'start',
      }}
    >
      {children}
    </div>
  );
}

function InfoBanner({ children }: { children: React.ReactNode }) {
  return (
    <NmCard
      variant="inset"
      style={{
        marginTop: 12,
        padding: 12,
        background: 'rgba(74,123,229,0.08)',
        borderColor: 'rgba(74,123,229,0.2)',
        fontSize: 12,
        color: 'var(--fg-2)',
        lineHeight: 1.55,
      }}
    >
      {children}
    </NmCard>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface AnnualPlanPageProps {
  user: UserProfile | null;
  effectiveUserId: number | null;
  athletes?: import('../services/api').AthleteItem[];
  layout?: 'desktop' | 'mobile';
}

export function AnnualPlanPage({ user, effectiveUserId, athletes = [], layout: _layout = 'desktop' }: AnnualPlanPageProps) {
  const { t, i18n } = useAnnualPlanTranslation();
  const fmtDate = (value: string) => displayDigits(formatDateShort(value));
  const userId = effectiveUserId ?? user?.id ?? null;
  const currentYear = new Date().getFullYear();

  /** Coach views use the selected athlete; athletes see their own profile. */
  const effectiveAthleteName = useMemo(() => {
    if (effectiveUserId && (athletes.length || user?.athletes?.length)) {
      const athlete = athletes.find(a => a.id === effectiveUserId) ?? user?.athletes?.find(a => a.id === effectiveUserId);
      if (athlete) return athlete.full_name;
    }
    return user?.name ?? user?.full_name ?? t('annualPlan.common.athlete');
  }, [athletes, effectiveUserId, t, user]);

  const effectiveAthleteAvatar = useMemo(() => {
    if (effectiveUserId && effectiveUserId !== user?.id) {
      const athlete = athletes.find(a => a.id === effectiveUserId) ?? user?.athletes?.find(a => a.id === effectiveUserId);
      return athlete?.profile_image;
    }
    return effectiveUserId && effectiveUserId !== user?.id ? undefined : user?.avatar ?? user?.profile_image;
  }, [athletes, effectiveUserId, user]);

  const [profilePhoto, setProfilePhoto] = useState<{ id: number; url?: string | null } | null>(null);
  useEffect(() => {
    if (!userId || effectiveAthleteAvatar) return;
    let active = true;
    void athleteSettingsApi.getAthleteDetails(userId).then(response => {
      if (active) setProfilePhoto({ id: userId, url: response.data.data.details.profile_image });
    }).catch(() => { /* Keep the athlete's initials when no photo is available. */ });
    return () => { active = false; };
  }, [userId, effectiveAthleteAvatar]);
  const athletePhotoUrl = effectiveAthleteAvatar ?? (profilePhoto?.id === userId ? profilePhoto.url : undefined);

  // Wizard
  const [step, setStep] = useState(0);
  const [planName, setPlanName] = useState(() =>
    t('annualPlan.wizard.defaultPlanName', { year: currentYear }),
  );
  const [startDateISO, setStartDateISO] = useState(todayISO);
  const [endDateISO, setEndDateISO] = useState('');
  const [ctlTarget, setCtlTarget] = useState(110);
  const [ctlBase, setCtlBase] = useState(65);
  const [recoveryCycle, setRecoveryCycle] = useState<3 | 4>(4);
  const [selectedModel, setSelectedModel] = useState<TrainingModelKey>('traditional');
  const [phaseWeekCounts, setPhaseWeekCounts] = useState<Record<string, number>>({
    ...DEFAULT_PHASE_COUNTS,
  });
  const [weeklyTSS, setWeeklyTSS] = useState<Record<string, number[]>>(() => ({
    ...DEFAULT_PHASE_WEEKS,
  }));
  const [sheetPhase, setSheetPhase] = useState<string | null>(null);
  const [tssLoadPercent, setTssLoadPercent] = useState(100);
  const tssPlanBaselineRef = useRef<Record<string, number[]> | null>(null);
  const planWeeksSyncKeyRef = useRef('');

  // Data
  const [atp, setAtp] = useState<ATPPlan | null>(null);
  const [weeks, setWeeks] = useState<ATPWeek[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showProgram, setShowProgram] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [trends, setTrends] = useState<TodayTrends | null>(null);
  const [seasonEvents, setSeasonEvents] = useState<CalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [eventModalDate, setEventModalDate] = useState(todayISO);
  const [deletingEventId, setDeletingEventId] = useState<number | null>(null);

  const phaseLabel = (phaseKey: string) => t(`annualPlan.phases.${phaseKey}`);
  const trainingModelLabel = (modelKey: string) =>
    t(`annualPlan.trainingModels.${modelKey}.label`, { defaultValue: modelKey });
  const trainingModelDescription = (modelKey: string) =>
    t(`annualPlan.trainingModels.${modelKey}.description`);

  function openCreateEventModal() {
    setEditingEvent(null);
    setEventModalDate(defaultEventDate);
    setShowEventModal(true);
  }

  function openEditEventModal(event: CalendarEvent) {
    setEditingEvent(event);
    setEventModalDate(eventDateISO(event));
    setShowEventModal(true);
  }

  function closeEventModal() {
    setShowEventModal(false);
    setEditingEvent(null);
  }

  const { totalWeeks, totalTSS, avgTSS } = computeTotals(phaseWeekCounts, weeklyTSS);

  /**
   * Live CTL from athlete home API (yesterday's training_metrics).
   * Falls back to ctlBase only when metrics are missing — never use stale ATP ctl_start.
   */
  const currentCtl = useMemo(() => {
    const live = trends?.ctl;
    if (live != null && Number.isFinite(Number(live)) && Number(live) >= 0) return Math.round(Number(live));
    if (ctlBase >= 0) return ctlBase;
    return 65;
  }, [trends?.ctl, ctlBase]);

  const ctlIncrease = ctlTarget - (atp ? ctlBase : currentCtl);

  const aRaceEvent = useMemo(
    () => seasonEvents.find(e => e.id === atp?.monitoring?.target_event_id && e.atp_priority === 1) ?? getFirstAPriorityEvent(seasonEvents),
    [seasonEvents, atp?.monitoring?.target_event_id],
  );

  const aRaceCtlMetrics = useMemo(() => {
    if (!aRaceEvent) return null;
    return computeARaceCtlMetrics({
      ctlBase: atp ? ctlBase : currentCtl,
      ctlTarget,
      planStartISO: startDateISO,
      aRaceDateISO: eventDateISO(aRaceEvent),
    });
  }, [aRaceEvent, atp, ctlBase, currentCtl, ctlTarget, startDateISO]);

  /** CTL/week ramp to first A-race (not total plan weeks). */
  const ctlRate = aRaceCtlMetrics
    ? aRaceCtlMetrics.ctlRampPerWeek.toFixed(1)
    : '0';

  /** Back-calculated weekly TSS to hit CTL target at A-race (TrainingPeaks CTL model). */
  const projectedWeeklyTss = aRaceCtlMetrics?.estimatedWeeklyTss ?? 0;

  const aRaceDateISO = aRaceEvent ? eventDateISO(aRaceEvent) : null;

  /** Total phase weeks must match season end when set, otherwise first A-race. */
  const requiredPlanWeeks = useMemo(
    () => resolveRequiredPlanWeeks(startDateISO, endDateISO || null, aRaceDateISO),
    [startDateISO, endDateISO, aRaceDateISO],
  );
  const planWeeksMatch =
    requiredPlanWeeks != null && totalWeeks === requiredPlanWeeks;

  /** Step 4 (Training Model): block Continue until phase weeks match A-race window. */
  const trainingModelContinueBlocked =
    step === 3 && requiredPlanWeeks != null && !planWeeksMatch;

  /** CTL projected at A-race from the current week-by-week TSS plan. */
  const projectedCtlAtRace = useMemo(() => {
    const series = flattenPhaseWeeklyTss(phaseWeekCounts, weeklyTSS);
    if (series.length === 0) return currentCtl;
    return projectCtlAtDate(atp ? ctlBase : currentCtl, series, startDateISO, aRaceDateISO ?? addDaysISO(startDateISO, series.length * 7));
  }, [atp, ctlBase, currentCtl, phaseWeekCounts, weeklyTSS, startDateISO, aRaceDateISO]);

  const projectedCtlGap = ctlTarget - projectedCtlAtRace;
  const projectedCtlRamp =
    requiredPlanWeeks != null && requiredPlanWeeks > 0
      ? ((projectedCtlAtRace - (atp ? ctlBase : currentCtl)) / requiredPlanWeeks).toFixed(1)
      : '0';

  const computedEndISO =
    endDateISO ||
    (startDateISO && totalWeeks > 0 ? addDaysISO(startDateISO, totalWeeks * 7) : '');

  const seasonRangeEndISO = computedEndISO || startDateISO;

  /** Default date when adding a race — today if inside season, otherwise season start. */
  const defaultEventDate = useMemo(() => {
    const today = todayISO();
    if (today >= startDateISO && today <= seasonRangeEndISO) return today;
    return startDateISO;
  }, [startDateISO, seasonRangeEndISO]);

  const fetchSeasonEvents = useCallback(async () => {
    if (!userId || !startDateISO || !seasonRangeEndISO) {
      setSeasonEvents([]);
      return;
    }
    setEventsLoading(true);
    try {
      let all: CalendarEvent[] = [];
      try {
        const listRes = await forms.getUserEventsInRange(userId, startDateISO, seasonRangeEndISO);
        all = extractSeasonEvents(listRes.data);
      } catch {
        // fall through to calendar
      }
      if (all.length === 0) {
        const calRes = await calendar.getCalendarData(userId, startDateISO, seasonRangeEndISO);
        all = extractSeasonEvents(null, calRes.data);
      }
      const today = todayISO();
      const upcoming = all
        .filter(ev => {
          const d = eventDateISO(ev);
          return d >= startDateISO && d <= seasonRangeEndISO;
        })
        .sort((a, b) => eventDateISO(a).localeCompare(eventDateISO(b)));
      setSeasonEvents(upcoming);
    } catch {
      pushToast(t('annualPlan.wizard.eventsLoadFailed'));
      setSeasonEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [userId, startDateISO, seasonRangeEndISO, t]);

  const confirmDeleteSeasonEvent = useCallback((ev: CalendarEvent) => {
    if (!window.confirm(t('eventForm.action.deleteConfirm', { name: ev.name }))) return;
    void (async () => {
      setDeletingEventId(ev.id);
      try {
        await forms.deleteEvent(ev.id);
        setSeasonEvents(prev => prev.filter(e => e.id !== ev.id));
        if (editingEvent?.id === ev.id) {
          setEditingEvent(null);
          setShowEventModal(false);
        }
        pushToast(t('eventForm.action.deleted'));
      } catch {
        pushToast(t('eventForm.error.deleteFailed'));
      } finally {
        setDeletingEventId(null);
      }
    })();
  }, [editingEvent?.id, t]);

  const fetchAtp = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await atpApi.getAtp(userId);
      if (result) {
        setAtp(result.atp);
        setWeeks(result.weeks);
        setPlanName(result.atp.name);
        setStartDateISO(planStartISO(result.atp, result.weeks));
        setEndDateISO(planEndISO(result.atp, result.weeks));
        setSelectedModel((result.atp.training_methodology as TrainingModelKey) || 'traditional');
        setCtlTarget(result.atp.ctl_constant ?? 110);
        setRecoveryCycle(result.atp.recovery_cycle ?? 4);
        if (result.atp.ctl_start != null && result.atp.ctl_start >= 0) {
          setCtlBase(result.atp.ctl_start);
        }
        const derived = derivePhaseStateFromWeeks(result.weeks);
        setPhaseWeekCounts(derived.phaseWeekCounts);
        setWeeklyTSS(derived.weeklyTSS);
        setShowProgram(true);
      } else {
        setAtp(null);
        setWeeks([]);
        setShowProgram(false);
      }
    } catch {
      pushToast(t('annualPlan.wizard.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void fetchAtp();
  }, [fetchAtp]);

  useEffect(() => {
    if (!userId) return;
    home
      .getHomeData(userId)
      .then(res => {
        const t = res.data.data.today_trends;
        setTrends(t);

      })
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    void fetchSeasonEvents();
  }, [fetchSeasonEvents]);

  /** When plan start, season end, or A-race date changes, sync phase week counts. */
  useEffect(() => {
    const syncKey = planWeeksSyncKey(
      startDateISO,
      endDateISO || null,
      aRaceDateISO,
      requiredPlanWeeks,
    );
    if (showProgram) { planWeeksSyncKeyRef.current = syncKey; return; }
    const prevKey = planWeeksSyncKeyRef.current;
    const keyChanged = prevKey !== syncKey;

    if (requiredPlanWeeks != null && keyChanged && prevKey !== '') {
      const rescaleTss = !atp && step >= 4 ? projectedWeeklyTss : undefined;
      setWeeklyTSS(prevTss => {
        const { phaseWeekCounts: distributed, weeklyTSS: nextTss } = redistributePlanWeeks(
          requiredPlanWeeks,
          prevTss,
          rescaleTss,
        );
        setPhaseWeekCounts(distributed);
        if (step >= 4) {
          tssPlanBaselineRef.current = Object.fromEntries(
            ATP_PHASES.map(ph => [ph.key, [...(nextTss[ph.key] ?? [])]]),
          );
          setTssLoadPercent(100);
        }
        return nextTss;
      });
    }

    if (keyChanged) {
      planWeeksSyncKeyRef.current = syncKey;
    }
  }, [startDateISO, endDateISO, aRaceDateISO, requiredPlanWeeks, step, projectedWeeklyTss, showProgram, atp]);

  /** When entering Training Model, align phases to plan span if dates changed. */
  const prevWizardStepRef = useRef(step);
  useEffect(() => {
    const enteredTrainingModel =
      step === 3 && prevWizardStepRef.current !== 3 && requiredPlanWeeks != null;

    if (enteredTrainingModel && totalWeeks !== requiredPlanWeeks) {
      const distributed = distributePhaseWeekCounts(requiredPlanWeeks);
      setPhaseWeekCounts(distributed);
      setWeeklyTSS(prev => resizeWeeklyTssForPhaseCounts(distributed, prev));
    }

    const enteredTssReview = step === 4 && prevWizardStepRef.current === 3;
    if (enteredTssReview) {
      setWeeklyTSS(prev => {
        const next = prepareReviewTss(phaseWeekCounts, prev, projectedWeeklyTss, !atp && !tssPlanBaselineRef.current);
        tssPlanBaselineRef.current = Object.fromEntries(
          ATP_PHASES.map(ph => [ph.key, [...(next[ph.key] ?? [])]]),
        );
        return next;
      });
      setTssLoadPercent(100);
    }

    prevWizardStepRef.current = step;
  }, [step, requiredPlanWeeks, projectedWeeklyTss, avgTSS, phaseWeekCounts, totalWeeks, atp]);

  function pushToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function setPhaseWeeks(phaseKey: string, value: number) {
    const newLen = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
    setPhaseWeekCounts(prev => ({ ...prev, [phaseKey]: newLen }));
    setWeeklyTSS(prev => {
      const defaults = DEFAULT_PHASE_WEEKS[phaseKey] ?? [];
      const arr = [...(prev[phaseKey] ?? defaults)];
      while (arr.length < newLen) arr.push(arr[arr.length - 1] ?? 500);
      return { ...prev, [phaseKey]: arr.slice(0, newLen) };
    });
  }

  function adjustPhaseWeeks(phaseKey: string, delta: number) {
    setPhaseWeeks(phaseKey, (phaseWeekCounts[phaseKey] ?? 0) + delta);
  }

  function adjustWeekTSS(phaseKey: string, weekIdx: number, delta: number) {
    setWeeklyTSS(prev => {
      const arr = [...(prev[phaseKey] ?? [])];
      arr[weekIdx] = Math.max(0, (arr[weekIdx] ?? 400) + delta);
      const next = { ...prev, [phaseKey]: arr };
      tssPlanBaselineRef.current = Object.fromEntries(
        ATP_PHASES.map(ph => [ph.key, [...(next[ph.key] ?? [])]]),
      );
      return next;
    });
    setTssLoadPercent(100);
  }

  function handleTssLoadChange(percent: number) {
    setTssLoadPercent(percent);
    const baseline = tssPlanBaselineRef.current;
    if (!baseline) return;
    setWeeklyTSS(scalePhaseWeeklyTss(phaseWeekCounts, baseline, percent / 100));
  }

  async function buildAtp() {
    if (!userId) return;
    if (!aRaceEvent || !aRaceDateISO || aRaceDateISO < startDateISO || !planWeeksMatch) { pushToast(t('annualPlan.monitor.invalidDates')); return; }
    setSaving(true);
    const isUpdate = Boolean(atp?.id);
    try {
      const phases = buildPhasesPayload(phaseWeekCounts, weeklyTSS);
      if (phases.some(p => p.weeks.some(v => !Number.isFinite(v) || v < 0 || v > 3000))) { pushToast(t('annualPlan.monitor.invalidLoad')); return; }
      const payload = {
        user_id: userId,
        name: planName,
        start_date: startDateISO,
        end_date: aRaceDateISO ?? computedEndISO,
        target_event_id: aRaceEvent?.id ?? null,
        recovery_cycle: recoveryCycle,
        ctl_start: atp ? ctlBase : currentCtl,
        ctl_target: ctlTarget,
        training_methodology: selectedModel,
        weekly_avg_tss: avgTSS,
        phases,
      };
      const result = isUpdate
        ? await atpApi.updateAtp(atp!.id, payload)
        : await atpApi.createAtp(payload);
      setAtp(result.atp);
      setWeeks(result.weeks);
      setEndDateISO(planEndISO(result.atp, result.weeks));
      setShowProgram(true);
      setStep(0);
      pushToast(
        isUpdate ? t('annualPlan.wizard.planUpdated') : t('annualPlan.wizard.planCreated'),
      );
    } catch {
      pushToast(
        isUpdate ? t('annualPlan.wizard.updateFailed') : t('annualPlan.wizard.createFailed'),
      );
    } finally {
      setSaving(false);
    }
  }

  /** Open the wizard with the current plan loaded for editing. */
  function handleEditAtp() {
    if (!atp) return;
    setStartDateISO(planStartISO(atp, weeks));
    setEndDateISO(planEndISO(atp, weeks));
    setPlanName(atp.name);
    setCtlTarget(atp.ctl_constant ?? ctlTarget);
    setCtlBase(atp.ctl_start);
    setRecoveryCycle(atp.recovery_cycle ?? 4);
    setSelectedModel((atp.training_methodology as TrainingModelKey) || selectedModel);
    if (weeks.length > 0) {
      const derived = derivePhaseStateFromWeeks(weeks);
      setPhaseWeekCounts(derived.phaseWeekCounts);
      setWeeklyTSS(derived.weeklyTSS);
    }
    setShowProgram(false);
    planWeeksSyncKeyRef.current = '';
    setStep(0);

  }

  function handleDeleted() {
    setAtp(null);
    setWeeks([]);
    setShowProgram(false);
    setStep(0);
    pushToast(t('annualPlan.wizard.planDeleted'));
  }

  // ── Step renderers ───────────────────────────────────────────────────────

  function renderStep0() {
    return (
      <div style={S.stepScroll} className="atp-wizard-step">
        <p style={S.stepSub}>{t('annualPlan.wizard.subtitles.initialInfo')}</p>

        <NmCard className="atp-athlete-summary" style={{ marginTop: 12 }}>
          <div style={S.profileRow}>
            <div style={S.profileTextCol}>
              <div style={{ fontWeight: 700, color: 'var(--fg-1)', textAlign: 'end' }}>{effectiveAthleteName}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-3)', textAlign: 'end' }}>
                {t('annualPlan.common.cyclist')}
              </div>
            </div>
            {athletePhotoUrl ? (
              <img
                src={athletePhotoUrl}
                alt=""
                style={{ ...S.avatar, objectFit: 'cover' }}
              />
            ) : (
              <div style={S.avatar}>
                {(effectiveAthleteName[0] ?? 'V').toUpperCase()}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <StatPill label={t('annualPlan.common.todayFitness')} value={String(currentCtl)} color="var(--text-info)" />


          </div>
        </NmCard>

        <NmCard style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <FieldLabel>{t('annualPlan.wizard.planName')}</FieldLabel>
            <NmInput style={SEASON_FIELD_STYLE} value={displayDigits(planName)} onChange={e => setPlanName(e.target.value)} />
          </div>
          <div>
            <FieldLabel>{t('annualPlan.wizard.seasonStart')}</FieldLabel>
            <NmIsoDateField
              ariaLabel={t('annualPlan.wizard.seasonStart')}
              style={SEASON_FIELD_STYLE}
              value={startDateISO}
              onChange={setStartDateISO}
            />
          </div>
          <div>
            <FieldLabel>{t('annualPlan.wizard.seasonEnd')}</FieldLabel>
            <NmIsoDateField
              ariaLabel={t('annualPlan.wizard.seasonEnd')}
              style={SEASON_FIELD_STYLE}
              value={endDateISO || computedEndISO}
              onChange={setEndDateISO}
            />
          </div>
        </NmCard>

        <details className="atp-explanation"><summary>{t('annualPlan.guide.button')}</summary><InfoBanner>
          {t('annualPlan.wizard.buildInfo', { ctl: currentCtl })}
        </InfoBanner></details>
      </div>
    );
  }

  function renderStep1() {
    return (
      <div style={S.stepScroll} className="atp-wizard-step">
        <p style={S.stepSub}>{t('annualPlan.wizard.subtitles.races')}</p>

        <NmCard style={{ marginTop: 16 }}>
          <FieldLabel>{t('annualPlan.wizard.priorityGuide')}</FieldLabel>
          <PriorityRow code="A" color="var(--text-danger)" desc={t('annualPlan.wizard.priorityA')} />
          <PriorityRow code="B" color="var(--text-warning)" desc={t('annualPlan.wizard.priorityB')} />
          <PriorityRow code="C" color="var(--fg-3)" desc={t('annualPlan.wizard.priorityC')} />
        </NmCard>

        <NmCard style={{ marginTop: 12 }}>
          <FieldLabel>
            {t('annualPlan.wizard.seasonEvents')}
            {seasonRangeEndISO && (
              <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>
                ({fmtDate(startDateISO)} – {fmtDate(seasonRangeEndISO)})
              </span>
            )}
          </FieldLabel>

          {eventsLoading ? (
            <div style={{ fontSize: 12, color: 'var(--fg-3)', padding: '12px 0' }}>
              <NmLoader size="sm" inline label={t('annualPlan.wizard.loadingEvents')} />
            </div>
          ) : seasonEvents.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-3)', padding: '12px 0', textAlign: 'center' }}>
              {t('annualPlan.wizard.noEvents')}
            </div>
          ) : (
            <>
              {!aRaceEvent && (
                <div style={{ fontSize: 12, color: 'var(--ds-text-danger)', padding: '8px 0 12px', textAlign: 'center', fontWeight: 600 }}>
                  {t('annualPlan.wizard.aRaceRequired')}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {seasonEvents.map(ev => (
                  <RaceEventRow
                    key={ev.id}
                    event={ev}
                    formatDate={fmtDate}
                    deleting={deletingEventId === ev.id}
                    onEdit={() => openEditEventModal(ev)}
                    onDelete={() => confirmDeleteSeasonEvent(ev)}
                  />
                ))}
              </div>
            </>
          )}

          <ActionButton
            type="button"
            disabled={!userId}
            onClick={openCreateEventModal}
            wrap
            variant="primary"
            icon={<ActionSymbol name="add" />}
            className="atp-add-race"
          >{t('annualPlan.wizard.addRace')}</ActionButton>
        </NmCard>

        <details className="atp-explanation"><summary>{t('annualPlan.guide.button')}</summary><InfoBanner>
          {t('annualPlan.wizard.eventsInfo')}
        </InfoBanner></details>
      </div>
    );
  }

  function renderStep2() {
    const rampNum = parseFloat(ctlRate);
    const rampSafe = rampNum <= 5;
    const rampModerate = rampNum > 5 && rampNum <= 8;

    return (
      <div style={S.stepScroll} className="atp-wizard-step">
        <p style={S.stepSub}>{t('annualPlan.wizard.subtitles.ctlTarget')}</p>

        {aRaceEvent && aRaceCtlMetrics && (
          <NmCard style={{ marginTop: 16 }}>
            <FieldLabel>{t('annualPlan.wizard.aRace')}</FieldLabel>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--fg-1)' }}>{displayDigits(aRaceEvent.name)}</div>
            <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
              {fmtDate(aRaceCtlMetrics.aRaceDateISO)}
              {' · '}
              {t('annualPlan.wizard.weeksFromStart', { count: Math.ceil(aRaceCtlMetrics.weeksToARace) })}
            </div>
          </NmCard>
        )}

        <NmCard style={{ marginTop: 12, textAlign: 'center', padding: '16px 12px 8px' }}>
          <FieldLabel>{t('annualPlan.ctlTarget.label')}</FieldLabel>
          <AtpCtlTargetGauge
            value={ctlTarget}
            min={CTL_GAUGE_MIN}
            max={CTL_GAUGE_MAX}
            onChange={setCtlTarget}
            subtitle={t('annualPlan.ctlTarget.subtitle')}
          />
        </NmCard>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <StatPill label={t('annualPlan.common.currentCtl')} value={String(currentCtl)} />
          <StatPill label={t('annualPlan.common.increase')} value={`${ctlIncrease > 0 ? '+' : ''}${ctlIncrease}`} color="var(--text-success)" />
          <StatPill label={t('annualPlan.common.ctlPerWeek')} value={ctlRate} color="var(--text-info)" />
          <StatPill label={t('annualPlan.common.tssPerWeek')} value={projectedWeeklyTss > 0 ? String(projectedWeeklyTss) : '—'} />
        </div>

        <details className="atp-explanation"><summary>{t('annualPlan.guide.button')}</summary><InfoBanner>
          {t('annualPlan.wizard.ctlInfo', {
            currentCtl,
            source:
              trends?.ctl != null && Number(trends.ctl) > 0
                ? t('annualPlan.wizard.ctlSourceHome')
                : t('annualPlan.wizard.ctlSourceFallback'),
            weeks: aRaceCtlMetrics ? Number(aRaceCtlMetrics.weeksToARace.toFixed(1)) : '—',
            rate: ctlRate,
            risk: rampSafe
              ? t('annualPlan.wizard.riskLow')
              : rampModerate
                ? t('annualPlan.wizard.riskModerate')
                : t('annualPlan.wizard.riskHigh'),
            target: ctlTarget,
          })}
        </InfoBanner></details>
      </div>
    );
  }

  function renderStep3() {
    return (
      <div style={S.stepScroll} className="atp-wizard-step">
        <div className="atp-wizard-recovery">
          <span className="atp-recovery-label">{t('annualPlan.monitor.recoveryCycle')}</span>
          <ActionToggleGroup<3 | 4>
            className="atp-recovery-toggle"
            label={t('annualPlan.monitor.recoveryCycle')}
            value={recoveryCycle}
            options={[{ value: 3, label: t('annualPlan.monitor.cycle3') }, { value: 4, label: t('annualPlan.monitor.cycle4') }]}
            onChange={next => {
              if (next === recoveryCycle) return;
              setWeeklyTSS(prev => Object.fromEntries(Object.entries(prev).map(([key, values]) => [key, values.map((v, i) => {
                if (key === 'peak' || key === 'taper') return v;
                const oldRecovery = (i + 1) % recoveryCycle === 0;
                const newRecovery = (i + 1) % next === 0;
                return Math.round(v * (oldRecovery ? 1 / 0.65 : 1) * (newRecovery ? 0.65 : 1));
              })])));
              setRecoveryCycle(next);
              }}
          />
        </div>
        <p style={S.stepSub}>{t('annualPlan.wizard.subtitles.trainingModel')}</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          {TRAINING_MODELS.map(m => (
            <button
              key={m.key}
              type="button"
              aria-pressed={selectedModel === m.key}
              className="atp-model-card"
              onClick={() => setSelectedModel(m.key)}
              style={{
                ...S.modelCard,

              }}
            >
              <span style={{ ...S.radio, ...(selectedModel === m.key ? S.radioSel : {}) }}>
                {selectedModel === m.key && <span style={S.radioDot} />}
              </span>
              <div style={{ flex: 1, textAlign: 'start' }}>
                <div style={{ fontWeight: 700, color: 'var(--fg-1)', fontSize: 13 }}>
                  {trainingModelLabel(m.key)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                  {trainingModelDescription(m.key)}
                </div>
              </div>
              <span style={{ fontSize: 22 }}>{m.emoji}</span>
            </button>
          ))}
        </div>

        <NmCard style={{ marginTop: 16 }}>
          <FieldLabel>{t('annualPlan.wizard.weeksPerPhase')}</FieldLabel>
          {ATP_PHASES.map(ph => (
            <div key={ph.key} style={S.phaseRow}>
              <span style={{ ...S.phaseDot, background: ph.color }} />
              <span style={{ flex: 1, fontWeight: 600, fontSize: 12 }}>{phaseLabel(ph.key)}</span>
              <div className="atp-week-stepper" role="group" aria-label={phaseLabel(ph.key)} dir="ltr">
                <ActionIconButton
                  type="button"
                  className="atp-week-stepper__button"
                  disabled={(phaseWeekCounts[ph.key] ?? 0) <= 0}
                  onClick={() => adjustPhaseWeeks(ph.key, -1)}
                  label={phaseLabel(ph.key) + ' −'}
                  icon={<ActionSymbol name="remove" />}
                />
                <input className="atp-week-stepper__value" type="text" inputMode="numeric" pattern="[0-9۰-۹٠-٩]*"
                  aria-label={phaseLabel(ph.key) + ' — ' + t('annualPlan.common.weeksShort')}
                  value={displayDigits(phaseWeekCounts[ph.key] ?? 0)}
                  onChange={e => setPhaseWeeks(ph.key, Number(normalizeInputDigits(e.currentTarget.value)))} />
                <ActionIconButton
                  type="button"
                  className="atp-week-stepper__button"
                  onClick={() => adjustPhaseWeeks(ph.key, 1)}
                  label={phaseLabel(ph.key) + ' +'}
                  icon={<ActionSymbol name="add" />}
                />
                <span className="atp-week-stepper__unit">{t('annualPlan.common.weeksShort')}</span>
              </div>
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px solid var(--surface-sunken)',
              fontSize: 12,
            }}
          >
            <span style={{ color: 'var(--fg-3)' }}>
              {t('annualPlan.common.total')}
              {requiredPlanWeeks != null && (
                <span style={{ fontWeight: 500, marginLeft: 6 }}>
                  {t('annualPlan.wizard.aRaceWeeks', { count: requiredPlanWeeks })}
                </span>
              )}
            </span>
            <span
              style={{
                fontWeight: 700,
                color:
                  requiredPlanWeeks == null || planWeeksMatch
                    ? 'var(--fg-1)'
                    : 'var(--text-danger)',
              }}
            >
              {t('annualPlan.common.weekCount', { count: totalWeeks })}
            </span>
          </div>
        </NmCard>

        {requiredPlanWeeks != null && !planWeeksMatch && (
          <NmCard
            variant="inset"
            style={{
              marginTop: 12,
              padding: 12,
              background: 'rgba(220,38,38,0.08)',
              borderColor: 'rgba(220,38,38,0.25)',
              fontSize: 12,
              color: 'var(--ds-text-danger)',
              fontWeight: 600,
              lineHeight: 1.55,
            }}
          >
            {totalWeeks < requiredPlanWeeks
              ? t('annualPlan.wizard.weeksShortMessage', {
                  count: requiredPlanWeeks - totalWeeks,
                  race: aRaceEvent?.name ?? t('annualPlan.wizard.aRace'),
                })
              : t('annualPlan.wizard.weeksOverMessage', {
                  count: totalWeeks - requiredPlanWeeks,
                  race: aRaceEvent?.name ?? t('annualPlan.wizard.aRace'),
                })}
          </NmCard>
        )}

        {requiredPlanWeeks != null && planWeeksMatch && (
          <details className="atp-explanation"><summary>{t('annualPlan.guide.button')}</summary><InfoBanner>
            {t('annualPlan.wizard.weeksAligned', {
              count: totalWeeks,
              race: aRaceEvent?.name ?? t('annualPlan.wizard.aRace'),
            })}
          </InfoBanner></details>
        )}
      </div>
    );
  }

  function renderStep4() {
    return (
      <div style={S.stepScroll} className="atp-wizard-step">
        <p style={S.stepSub}>{t('annualPlan.wizard.subtitles.tssReview')}</p>

        <NmCard style={{ marginTop: 16 }}>
          <div className="atp-review-grid atp-review-heading" style={S.tssHeaderRow}>
            <span style={{ flex: 2 }}>{t('annualPlan.common.phase')}</span>
            <span style={{ flex: 1, textAlign: 'center' }}>{t('annualPlan.common.weeksShort')}</span>
            <span style={{ flex: 1.5, textAlign: 'right' }}>{t('annualPlan.common.total')}</span>
            <span style={{ flex: 1.5, textAlign: 'right' }}>{t('annualPlan.common.average')}</span>
            <span style={{ width: 16 }} />
          </div>
          {ATP_PHASES.map(ph => {
            const phWeeks = (weeklyTSS[ph.key] ?? []).slice(0, phaseWeekCounts[ph.key]);
            const phTotal = phWeeks.reduce((a, b) => a + b, 0);
            const phAvg = phWeeks.length ? Math.round(phTotal / phWeeks.length) : 0;
            return (
              <button
                key={ph.key}
                type="button"
                className="atp-review-grid" style={S.tssRow}
                onClick={() => setSheetPhase(ph.key)}
              >
                <span style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...S.phaseDot, background: ph.color }} />
                  {phaseLabel(ph.key)}
                </span>
                <span style={{ flex: 1, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
                  {displayDigits(phaseWeekCounts[ph.key])}
                </span>
                <span style={{ flex: 1.5, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                  {displayDigits(phTotal.toLocaleString())}
                </span>
                <span style={{ flex: 1.5, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                  {displayDigits(phAvg)}
                </span>
                <span style={{ color: 'var(--fg-4)' }}>›</span>
              </button>
            );
          })}
          <div className="atp-review-grid" style={{ ...S.tssRow, borderTop: '1px solid var(--surface-sunken)', fontWeight: 700 }}>
            <span style={{ flex: 2 }}>{t('annualPlan.common.total')}</span>
            <span style={{ flex: 1, textAlign: 'center' }}>{displayDigits(totalWeeks)}</span>
            <span style={{ flex: 1.5, textAlign: 'right' }}>{displayDigits(totalTSS.toLocaleString())}</span>
            <span style={{ flex: 1.5, textAlign: 'right' }}>{displayDigits(avgTSS)}</span>
            <span style={{ width: 16 }} />
          </div>
          <div style={{ display: 'flex', height: 5, borderRadius: 99, overflow: 'hidden', marginTop: 12 }}>
            {ATP_PHASES.map(ph => {
              const phWeeks = (weeklyTSS[ph.key] ?? []).slice(0, phaseWeekCounts[ph.key]);
              const phTotal = phWeeks.reduce((a, b) => a + b, 0);
              const w = totalTSS > 0 ? (phTotal / totalTSS) * 100 : 0;
              return <div key={ph.key} style={{ width: `${w}%`, background: ph.color, height: '100%' }} />;
            })}
          </div>
        </NmCard>

        <WeeklyLoadSliderCard
          tssLoadPercent={tssLoadPercent}
          avgTSS={avgTSS}
          onChange={handleTssLoadChange}
          style={{ marginTop: 16 }}
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <StatPill label={t('annualPlan.common.currentCtl')} value={String(currentCtl)} />
          <StatPill
            label={t('annualPlan.common.projectedCtl')}
            value={String(projectedCtlAtRace)}
            color={projectedCtlAtRace >= ctlTarget ? 'var(--text-success)' : 'var(--text-warning)'}
          />
          <StatPill label={t('annualPlan.common.targetCtl')} value={String(ctlTarget)} color="var(--text-info)" />
          <StatPill
            label={t('annualPlan.common.gap')}
            value={`${projectedCtlGap >= 0 ? '+' : ''}${projectedCtlGap}`}
            color={projectedCtlGap <= 0 ? 'var(--text-success)' : 'var(--text-warning)'}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <StatPill label={t('annualPlan.common.ctlPerWeek')} value={projectedCtlRamp} />
          <StatPill label={t('annualPlan.common.avgTssPerWeek')} value={String(avgTSS || '—')} />
        </div>

        <details className="atp-explanation"><summary>{t('annualPlan.guide.button')}</summary><InfoBanner>
          {projectedCtlAtRace >= ctlTarget
            ? t('annualPlan.wizard.onTarget', { projected: projectedCtlAtRace, target: ctlTarget })
            : t('annualPlan.wizard.belowTarget', {
                projected: projectedCtlAtRace,
                gap: Math.abs(projectedCtlGap),
                target: ctlTarget,
              })}
        </InfoBanner></details>
      </div>
    );
  }

  function renderStep5() {
    const model = TRAINING_MODELS.find(m => m.key === selectedModel);
    return (
      <div style={S.stepScroll} className="atp-wizard-step">
        <p style={S.stepSub}>{t('annualPlan.wizard.subtitles.confirmBuild')}</p>

        <div className="atp-confirm-hero" style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={S.checkRing}>✓</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--fg-1)', marginTop: 10 }}>
            {t('annualPlan.wizard.readyToBuild')}
          </div>
        </div>

        <NmCard>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 28 }}>🏅</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{displayDigits(planName)}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                {trainingModelLabel(model?.key ?? selectedModel)}
              </div>
            </div>
          </div>
          <ConfirmRow label={t('annualPlan.wizard.start')} value={fmtDate(startDateISO)} />
          <ConfirmRow label={t('annualPlan.wizard.duration')} value={t('annualPlan.common.weekCount', { count: totalWeeks })} />
          <ConfirmRow label={t('annualPlan.common.targetCtl')} value={t('annualPlan.wizard.ctlTargetFrom', { target: ctlTarget, current: atp ? ctlBase : currentCtl })} />
          {aRaceEvent && (
            <ConfirmRow label={t('annualPlan.wizard.aRace')} value={`${displayDigits(aRaceEvent.name)} · ${fmtDate(eventDateISO(aRaceEvent))}`} />
          )}
          <ConfirmRow label={t('annualPlan.wizard.ctlRamp')} value={t('annualPlan.wizard.perWeekValue', { value: ctlRate })} />
          <ConfirmRow label={t('annualPlan.totalTss')} value={displayDigits(totalTSS.toLocaleString())} />
          <ConfirmRow label={t('annualPlan.common.avgTssPerWeek')} value={String(avgTSS)} />
        </NmCard>

        <NmCard style={{ marginTop: 12 }}>
          <FieldLabel>{t('annualPlan.wizard.phaseOverview')}</FieldLabel>
          {ATP_PHASES.map(ph => {
            const phWeeks = (weeklyTSS[ph.key] ?? []).slice(0, phaseWeekCounts[ph.key]);
            const phTotal = phWeeks.reduce((a, b) => a + b, 0);
            return (
              <div key={ph.key} className="atp-confirm-phase" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                <span style={{ ...S.phaseDot, background: ph.color }} />
                <span style={{ flex: 1, fontSize: 12 }}>{phaseLabel(ph.key)}</span>
                <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {t('annualPlan.common.weekCount', { count: phaseWeekCounts[ph.key] })}
                </span>
                <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  {displayDigits(phTotal.toLocaleString())}
                </span>
              </div>
            );
          })}
        </NmCard>
      </div>
    );
  }

  const STEP_RENDERERS = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  if (loading) {
    return (
      <div style={S.root} className="atp-plan-shell">
        <NmLoadingOverlay message={t('annualPlan.wizard.loading')} variant="page" />
      </div>
    );
  }

  return (
    <div style={S.root} className="atp-plan-shell">
      {/* Wizard layer */}
      {!(showProgram && atp) && <>
      <div style={S.navRow} className="atp-wizard-nav">
        {step > 0 ? (
          <ActionIconButton
            type="button"
            className="atp-wizard-back"
            onClick={() => setStep(s => s - 1)}
            label={t('annualPlan.common.back')}
            icon={<IoArrowBackOutline aria-hidden="true" style={{ transform: i18n.dir() === "rtl" ? "rotate(180deg)" : undefined }} />}
          />
        ) : (
          <div style={{ width: 36 }} aria-hidden />
        )}
        <span style={S.navTitle}>{t(`annualPlan.wizard.steps.${WIZARD_STEP_KEYS[step]}`)}</span>
        <AnnualPlanGuide step={step} />
      </div>

      <StepDots step={step} total={TOTAL_WIZARD_STEPS} />
      <p style={S.stepLbl}>
        {t('annualPlan.wizard.stepOf', { current: step + 1, total: TOTAL_WIZARD_STEPS })}
      </p>

      <div key={step} style={S.stepBody}>{STEP_RENDERERS[step]()}</div>

      <div style={S.bottomBar} className="atp-wizard-actions">
        <NmButton
          variant={step === TOTAL_WIZARD_STEPS - 1 ? 'primary' : 'primary'}
          loading={saving}
          disabled={trainingModelContinueBlocked}
          onClick={() => {
            if (step === 1 && !aRaceEvent) {
              pushToast(t('annualPlan.wizard.aRaceRequired'));
              return;
            }
            if (step === TOTAL_WIZARD_STEPS - 1) void buildAtp();
            else setStep(s => Math.min(TOTAL_WIZARD_STEPS - 1, s + 1));
          }}
          style={{ width: '100%' }}
        >
          {step === TOTAL_WIZARD_STEPS - 1
            ? saving
              ? atp
                ? t('annualPlan.wizard.updating')
                : t('annualPlan.wizard.building')
              : atp
                ? t('annualPlan.wizard.updateAtp')
                : t('annualPlan.wizard.buildAtp')
            : t('annualPlan.wizard.continue')}
        </NmButton>
        {atp && step === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <NmButton variant="ghost" onClick={() => setShowProgram(true)} style={{ width: '100%' }}>
              {t('annualPlan.wizard.viewCurrentPlan')}
            </NmButton>
          </div>
        )}
      </div>

      </>}
      {/* Program overlay */}
      {showProgram && atp && (
        <AtpProgramView
          key={`${atp.id}-${userId}`}
          atp={atp}
          events={seasonEvents}
          onPlanUpdated={setAtp}
          weeks={weeks}
          phaseWeekCounts={phaseWeekCounts}
          weeklyTSS={weeklyTSS}
          onEdit={handleEditAtp}
          onDeleted={handleDeleted}
          onWeeksUpdated={setWeeks}
          onToast={pushToast}
        />
      )}

      {/* Phase TSS sheet */}
      {sheetPhase && (
        <PhaseTssSheet
          phaseKey={sheetPhase}
          phaseWeekCounts={phaseWeekCounts}
          weeklyTSS={weeklyTSS}
          planStartISO={startDateISO}
          recoveryCycle={recoveryCycle}
          onAdjustTss={adjustWeekTSS}
          onClose={() => setSheetPhase(null)}
        />
      )}

      {showEventModal && userId && (
        <EventFormModal
          date={eventModalDate}
          userId={userId}
          event={editingEvent}
          editableDate
          showPriority
          minDate={startDateISO}
          maxDate={seasonRangeEndISO}
          onClose={closeEventModal}
          onDeleted={() => {
            closeEventModal();
            void fetchSeasonEvents();
            pushToast(t('eventForm.action.deleted'));
          }}
          onSuccess={() => {
            const wasEdit = editingEvent != null;
            closeEventModal();
            void fetchSeasonEvents();
            pushToast(wasEdit ? t('eventForm.action.updated') : t('eventForm.action.created'));
          }}
        />
      )}

      {toast && <div className="atp-wizard-toast" style={S.toast}>{toast}</div>}
    </div>
  );
}

const PRIORITY_META: Record<number, { code: string; color: string }> = {
  1: { code: 'A', color: 'var(--text-danger)' },
  2: { code: 'B', color: 'var(--text-warning)' },
  3: { code: 'C', color: 'var(--fg-3)' },
};

function RaceEventRow({
  event,
  onEdit,
  onDelete,
  formatDate,
  deleting,
}: {
  event: CalendarEvent;
  onEdit: () => void;
  onDelete: () => void;
  formatDate: (iso: string) => string;
  deleting?: boolean;
}) {
  const { t } = useAnnualPlanTranslation();
  const priority = event.atp_priority != null ? PRIORITY_META[event.atp_priority] : null;
  return (
    <div style={S.raceRowWrap}>
      <button type="button" style={S.raceRow} onClick={onEdit} disabled={deleting} title={t('eventForm.headerEditLabel')}>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayDigits(event.name)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
            {formatDate(eventDateISO(event))}
          </div>
        </div>
        {priority ? (
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 12,
              color: priority.color,
              background: `color-mix(in srgb, ${priority.color} 13%, transparent)`,
              border: `1px solid color-mix(in srgb, ${priority.color} 35%, transparent)`,
              flexShrink: 0,
            }}
          >
            {priority.code}
          </span>
        ) : (
          <span style={{ fontSize: 10, color: 'var(--fg-4)', flexShrink: 0 }}>—</span>
        )}
        <span style={{ fontSize: 14, color: 'var(--fg-4)', flexShrink: 0 }} aria-hidden>›</span>
      </button>
      <ActionIconButton
        type="button"
        onClick={onDelete}
        disabled={deleting}
        title={t('eventForm.action.delete')}
        label={t('eventForm.action.delete')}
        icon={<IoTrashOutline aria-hidden="true" />}
        variant="danger"
      />
    </div>
  );
}

function PriorityRow({ code, color, desc }: { code: string; color: string; desc: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          color,
          background: `color-mix(in srgb, ${color} 13%, transparent)`,
          border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
        }}
      >
        {code}
      </span>
      <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{desc}</span>
    </div>
  );
}

function ConfirmRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="atp-confirm-row"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: '1px solid var(--surface-sunken)',
        fontSize: 12,
      }}
    >
      <span style={{ color: 'var(--fg-3)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: 'var(--fg-1)' }}>{displayDigits(value)}</span>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 4px',
    flexShrink: 0,
  },
  navTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 700,
    fontFamily: 'var(--font-display)',
    color: 'var(--fg-1)',
  },
  stepLbl: {
    textAlign: 'center',
    fontSize: 10,
    color: 'var(--fg-4)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    margin: '0 0 8px',
  },
  stepBody: {
    flex: 1,
    overflow: 'hidden',
    padding: '0 4px',
  },
  stepScroll: {
    height: '100%',
    overflowY: 'auto',
    paddingBottom: 16,
  },
  stepTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 800,
    fontFamily: 'var(--font-display)',
    color: 'var(--fg-1)',
  },
  stepSub: {
    margin: '4px 0 0',
    fontSize: 12,
    color: 'var(--fg-3)',
  },
  bottomBar: {
    flexShrink: 0,
    padding: '12px 4px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    borderTop: '1px solid var(--surface-sunken)',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 99,
    background: 'var(--interaction-accent-bg)',
    color: 'var(--interaction-accent-ink)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 18,
    flexShrink: 0,
  },
  profileRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    direction: 'ltr',
  },
  profileTextCol: {
    flex: 1,
    minWidth: 0,
  },
  raceRowWrap: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
    width: '100%',
  },
  raceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
    padding: '10px 12px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--surface-sunken)',
    boxShadow: 'var(--nm-flat)',
    cursor: 'pointer',
    textAlign: 'start',
    fontFamily: 'var(--font-body)',
    transition: 'box-shadow 0.12s',
  },
  ctlHero: {
    fontSize: 48,
    fontWeight: 800,
    color: 'var(--color-accent)',
    fontFamily: 'var(--font-display)',
    letterSpacing: -2,
  },
  modelCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    cursor: 'pointer',
    textAlign: 'start',
  },
  radio: {
    width: 18,
    height: 18,
    borderRadius: 99,
    border: '2px solid var(--surface-sunken)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioSel: { borderColor: 'var(--interaction-accent-mark)' },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 99,
    background: 'var(--color-accent)',
  },
  phaseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid var(--surface-sunken)',
  },
  phaseDot: { width: 8, height: 8, borderRadius: 99, flexShrink: 0 },
  tssHeaderRow: {
    display: 'flex',
    fontSize: 9,
    fontWeight: 700,
    color: 'var(--fg-4)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    paddingBottom: 8,
  },
  tssRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 0',
    borderTop: '1px solid var(--surface-sunken)',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    color: 'var(--fg-1)',
    textAlign: 'start',
  },
  checkRing: {
    width: 56,
    height: 56,
    borderRadius: 99,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    color: 'var(--ds-text-success)',
    background: 'rgba(31,174,99,0.12)',
    border: '2px solid rgba(31,174,99,0.35)',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    background: 'var(--surface-1)',
    color: 'var(--fg-1)',
    padding: '9px 16px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
    zIndex: 9999,
  },
};

const SEASON_FIELD_STYLE: React.CSSProperties = { width: '100%', height: 48, minHeight: 48, boxSizing: 'border-box', border: 'none', borderRadius: 'var(--r-3)', padding: '12px 16px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-body)', lineHeight: '24px', boxShadow: 'var(--nm-inset)' };
